import { Link } from "react-router-dom";
import s from "./platformAdmin.module.css";

export function formatRub(n) {
  return new Intl.NumberFormat("ru-RU").format(Number(n) || 0);
}

export function formatWeekLabel(isoWeek) {
  if (!isoWeek) return "";
  const d = new Date(isoWeek);
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
}

/** Краткие расшифровки метрик платформы (для hover / title). */
export const METRIC_TIPS = {
  mrr: "MRR (Monthly Recurring Revenue) — ежемесячная выручка с подписок клубов. Сумма текущих месячных платежей активных и пробных клубов.",
  arr: "ARR (Annual Recurring Revenue) — годовая выручка с подписок: MRR × 12.",
  mrr_30: "Прогноз MRR через 30 дней по текущему темпу роста числа клубов.",
  mrr_90: "Прогноз MRR через 90 дней (экстраполяция месячного роста на квартал).",
  arr_forecast: "Прогноз годовой выручки (ARR) на основе прогнозируемого MRR.",
  payments_30: "Сумма подтверждённых SaaS-оплат клубов за последние 30 дней.",
  clubs: "Всего клубов на платформе, включая пробные и активные.",
  new_30: "Сколько новых клубов зарегистрировалось за последние 30 дней.",
  health:
    "Индекс здоровья платформы 0–100: доля активных клубов, новые регистрации, входы сотрудников и число тренировок.",
  utilization:
    "Утилизация — доля клубов, которые провели хотя бы одно занятие за 30 дней.",
  new_clubs_forecast:
    "Сколько новых клубов ожидается за ближайший месяц по тренду последних 12 недель.",
  new_clients_forecast:
    "Прогноз числа новых спортсменов на всех клубах за ближайший месяц.",
  sessions_forecast:
    "Прогноз числа тренировок на платформе за ближайший месяц.",
  trial_paid:
    "Сколько оплат ожидается из текущих пробных клубов при средней конверсии trial → оплата.",
  trial: "Trial — пробный период, клуб ещё не оплатил подписку.",
  saas_funnel:
    "Воронка продаж SaaS: путь клуба от регистрации до оплаты и удержания на платформе.",
  churn_risk:
    "Клубы с истекающей или истёкшей подпиской и неактивные trial — высокий риск оттока.",
  expiring: "Подписка клуба заканчивается в ближайшие 14 дней.",
  inactive_trials: "Пробные клубы без занятий более 14 дней.",
  funnel_registered: "Все клубы на платформе, кроме отключённых.",
  funnel_engaged: "Клубы, которые уже начали пользоваться платформой (тариф, вход или занятия).",
  funnel_activated: "Клубы, у которых были тренировки за последние 30 дней.",
  funnel_paid: "Клубы с оплаченной подпиской (не на пробном периоде).",
  funnel_retained: "Оплатившие клубы, которые продолжают проводить занятия.",
  plan_mrr: "Вклад этого тарифа в общий MRR — ежемесячную выручку с подписок.",
};

const TREND_LABEL = {
  up: "↑ рост",
  down: "↓ снижение",
  stable: "→ стабильно",
  flat: "→ стабильно",
};

export function InfoTip({ text, label }) {
  if (!text) return null;
  return (
    <span className={s.infoTip} tabIndex={0} aria-label={label || text}>
      <span className={s.infoTipIcon} aria-hidden>
        ?
      </span>
      <span className={s.infoTipBubble} role="tooltip">
        {text}
      </span>
    </span>
  );
}

export function LabelWithTip({ children, tip, className }) {
  return (
    <div className={`${s.labelWithTip} ${className || ""}`}>
      <span>{children}</span>
      {tip ? <InfoTip text={tip} label={`Что значит: ${children}`} /> : null}
    </div>
  );
}

export function MetricCard({
  label,
  value,
  hint,
  trend,
  accent,
  forecast,
  tip,
}) {
  return (
    <article
      className={`${s.metricCard} ${accent ? s.metricCardAccent : ""}`}
      title={tip || undefined}
    >
      <LabelWithTip tip={tip} className={s.metricLabel}>
        {label}
      </LabelWithTip>
      <div className={s.metricValue}>{value}</div>
      {hint ? <div className={s.metricHint}>{hint}</div> : null}
      {forecast ? (
        <div className={s.metricForecast}>{forecast}</div>
      ) : null}
      {trend ? (
        <div
          className={`${s.metricTrend} ${
            trend === "up"
              ? s.metricTrendUp
              : trend === "down"
                ? s.metricTrendDown
                : ""
          }`}
        >
          {TREND_LABEL[trend] || TREND_LABEL.stable}
        </div>
      ) : null}
    </article>
  );
}

export function ForecastCard({
  label,
  value,
  suffix = "",
  hint,
  trend,
  accent,
  tip,
}) {
  return (
    <article
      className={`${s.forecastCard} ${accent ? s.forecastCardAccent : ""}`}
      title={tip || undefined}
    >
      <LabelWithTip tip={tip} className={s.forecastCardLabel}>
        {label}
      </LabelWithTip>
      <div className={s.forecastCardValue}>
        {value}
        {suffix ? <span className={s.forecastCardSuffix}>{suffix}</span> : null}
      </div>
      {hint ? <div className={s.forecastCardHint}>{hint}</div> : null}
      {trend ? (
        <div
          className={`${s.metricTrend} ${
            trend === "up"
              ? s.metricTrendUp
              : trend === "down"
                ? s.metricTrendDown
                : ""
          }`}
        >
          {TREND_LABEL[trend] || TREND_LABEL.stable}
        </div>
      ) : null}
    </article>
  );
}

const FUNNEL_COLORS = [
  "#0f766e",
  "#0d9488",
  "#2563eb",
  "#7c3aed",
  "#ca8a04",
];

const FUNNEL_STAGE_TIPS = {
  registered: METRIC_TIPS.funnel_registered,
  engaged: METRIC_TIPS.funnel_engaged,
  activated: METRIC_TIPS.funnel_activated,
  paid: METRIC_TIPS.funnel_paid,
  retained: METRIC_TIPS.funnel_retained,
};

export function SaasFunnel({ funnel }) {
  const stages = funnel?.stages || [];
  const steps = funnel?.conversion_steps || [];
  const maxCount = Math.max(...stages.map((st) => st.count), 1);

  if (!stages.length) {
    return <p className={s.emptyHint}>Нет данных для воронки</p>;
  }

  return (
    <div className={s.funnelWrap}>
      <div className={s.funnelStages}>
        {stages.map((stage, i) => {
          const width = Math.max(10, (stage.count / maxCount) * 100);
          const stageTip = FUNNEL_STAGE_TIPS[stage.id];
          return (
            <div
              key={stage.id}
              className={s.funnelStage}
              title={stageTip || undefined}
            >
              <div className={s.funnelStageMeta}>
                <LabelWithTip tip={stageTip} className={s.funnelStageLabel}>
                  {stage.label}
                </LabelWithTip>
                <strong className={s.funnelStageCount}>{stage.count}</strong>
              </div>
              <div className={s.funnelTrack}>
                <div
                  className={s.funnelFill}
                  style={{
                    width: `${width}%`,
                    background: FUNNEL_COLORS[i % FUNNEL_COLORS.length],
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {steps.length ? (
        <div className={s.funnelConvGrid}>
          {steps.map((step) => (
            <div
              key={`${step.from}-${step.to}`}
              className={s.funnelConvCard}
              title={`Доля клубов, перешедших с этапа «${step.from_label}» на «${step.to_label}».`}
            >
              <span className={s.funnelConvLabel}>
                {step.from_label} → {step.to_label}
              </span>
              <strong className={s.funnelConvRate}>
                {step.rate == null ? "—" : `${step.rate}%`}
              </strong>
            </div>
          ))}
        </div>
      ) : null}

      <div className={s.funnelFooter}>
        <span title={METRIC_TIPS.churn_risk}>
          В риске: <strong>{funnel?.at_risk_count ?? 0}</strong>
        </span>
        <span title="Клубы с истёкшим демо или просроченной оплатой.">
          Потеряны: <strong>{funnel?.lost_count ?? 0}</strong>
        </span>
        <span title="Клубы со статусом suspended — доступ отключён.">
          Отключены: <strong>{funnel?.suspended_count ?? 0}</strong>
        </span>
      </div>
    </div>
  );
}

const INSIGHT_CLASS = {
  positive: "insightPositive",
  warning: "insightWarning",
  neutral: "insightNeutral",
};

export function InsightList({ items = [] }) {
  if (!items.length) return null;
  return (
    <div className={s.insightList}>
      {items.map((item, i) => (
        <div
          key={`${item.type}-${i}`}
          className={`${s.insightItem} ${s[INSIGHT_CLASS[item.type] || "insightNeutral"]}`}
        >
          {item.text}
        </div>
      ))}
    </div>
  );
}

export function HealthRing({ score = 0 }) {
  const pct = Math.min(100, Math.max(0, score));
  return (
    <div
      className={s.healthRing}
      style={{ "--pct": pct }}
      title={METRIC_TIPS.health}
    >
      <div className={s.healthRingInner}>
        <strong>{pct}</strong>
        <span>health</span>
      </div>
    </div>
  );
}

export function TopClubsTable({ title, rows = [], valueKey, valueLabel }) {
  if (!rows.length) return null;
  return (
    <div className={s.chartPanel}>
      <h3 className={s.panelTitle}>{title}</h3>
      <div className={s.topList}>
        {rows.map((row, idx) => (
          <Link key={row.id} to={`/admin/tenants/${row.id}`} className={s.topRow}>
            <span className={s.topRank}>{idx + 1}</span>
            <div className={s.topMeta}>
              <strong>{row.name}</strong>
              <small>{row.subdomain}</small>
            </div>
            <span className={s.topValue}>
              {valueKey === "revenue_30d"
                ? `${formatRub(row[valueKey])} ₽`
                : row[valueKey]}
              {valueLabel ? ` ${valueLabel}` : ""}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

const PLAN_CARD_CLASS = {
  demo: "planCardDemo",
  basic: "planCardBasic",
  pro: "planCardPro",
  business: "planCardBusiness",
  other: "planCardOther",
};

export function PlanBucketCard({ bucket, label, count, mrrRub }) {
  const cardClass = PLAN_CARD_CLASS[bucket] || PLAN_CARD_CLASS.other;
  return (
    <article className={`${s.planCard} ${s[cardClass]}`}>
      <div className={s.planCardLabel}>{label}</div>
      <div className={s.planCardValue}>{count}</div>
      {mrrRub > 0 ? (
        <div className={s.planCardHint} title={METRIC_TIPS.plan_mrr}>
          MRR {formatRub(mrrRub)} ₽
        </div>
      ) : (
        <div className={s.planCardHint}>клубов</div>
      )}
    </article>
  );
}

function formatDateRu(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function daysLeftClass(days) {
  if (days == null) return "";
  if (days < 0) return s.daysExpired;
  if (days <= 3) return s.daysUrgent;
  if (days <= 14) return s.daysWarning;
  return "";
}

function planBadgeClass(bucket) {
  const map = {
    basic: s.planBadgeBasic,
    pro: s.planBadgePro,
    business: s.planBadgeBusiness,
    other: s.planBadgeOther,
  };
  return map[bucket] || s.planBadgeOther;
}

function statusPillClass(row) {
  if (row.status === "suspended") return s.statusSuspended;
  if (row.is_demo_expired) return s.statusExpired;
  if (row.is_trial) return s.statusTrial;
  if (row.is_paid) return s.statusActive;
  return s.statusNeutral;
}

export function ChartTooltipBox({ active, payload, label, valueFormatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className={s.chartTooltip}>
      <div className={s.chartTooltipLabel}>{label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} className={s.chartTooltipRow}>
          <span
            className={s.chartTooltipDot}
            style={{ background: entry.color || entry.stroke || "#0f766e" }}
          />
          <span>{entry.name || entry.dataKey}</span>
          <strong>
            {valueFormatter
              ? valueFormatter(entry.value, entry)
              : entry.value}
          </strong>
        </div>
      ))}
    </div>
  );
}

export function SubscriptionsTable({
  title,
  rows = [],
  emptyText = "Нет записей",
  showClients = true,
}) {
  return (
    <div className={s.chartPanel}>
      {title ? <h3 className={s.panelTitle}>{title}</h3> : null}
      {!rows.length ? (
        <p className={s.emptyHint}>{emptyText}</p>
      ) : (
        <>
          <div className={s.mobileCardList}>
            {rows.map((row) => (
              <Link
                key={row.id}
                to={`/admin/tenants/${row.id}`}
                className={s.mobileDataCard}
              >
                <div className={s.mobileDataCardTop}>
                  <strong>{row.name}</strong>
                  <span className={`${s.statusPill} ${statusPillClass(row)}`}>
                    {row.is_demo_expired ? "Демо истекло" : row.status_label}
                  </span>
                </div>
                <small className={s.mobileDataCardSub}>{row.subdomain}</small>
                <div className={s.mobileDataCardMeta}>
                  <span>{row.plan_name}</span>
                  <span className={`${s.daysLeft} ${daysLeftClass(row.days_left)}`}>
                    {row.days_left == null
                      ? "—"
                      : row.days_left < 0
                        ? `${Math.abs(row.days_left)} дн. назад`
                        : `${row.days_left} дн.`}
                  </span>
                </div>
                <div className={s.mobileDataCardMeta}>
                  <span>до {formatDateRu(row.paid_until)}</span>
                  <span>
                    {row.monthly_price_rub
                      ? `${formatRub(row.monthly_price_rub)} ₽/мес`
                      : "—"}
                  </span>
                </div>
                {showClients ? (
                  <div className={s.mobileDataCardMeta}>
                    <span>Спортсмены: {row.clients ?? 0}</span>
                  </div>
                ) : null}
              </Link>
            ))}
          </div>

          <div className={`${s.subTableWrap} ${s.desktopOnlyBlock}`}>
            <table className={s.subTable}>
              <thead>
                <tr>
                  <th>Клуб</th>
                  <th>Тариф</th>
                  <th>Статус</th>
                  <th>Оплачено до</th>
                  <th>Осталось</th>
                  <th>Цена</th>
                  {showClients ? <th>Спортсмены</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link
                        to={`/admin/tenants/${row.id}`}
                        className={s.subClubLink}
                      >
                        <strong>{row.name}</strong>
                        <small>{row.subdomain}</small>
                      </Link>
                    </td>
                    <td>
                      <span
                        className={`${s.planBadge} ${planBadgeClass(row.plan_bucket)}`}
                      >
                        {row.plan_name}
                      </span>
                    </td>
                    <td>
                      <span className={`${s.statusPill} ${statusPillClass(row)}`}>
                        {row.is_demo_expired
                          ? "Демо истекло"
                          : row.status_label}
                      </span>
                    </td>
                    <td>{formatDateRu(row.paid_until)}</td>
                    <td>
                      <span className={`${s.daysLeft} ${daysLeftClass(row.days_left)}`}>
                        {row.days_left == null
                          ? "—"
                          : row.days_left < 0
                            ? `${Math.abs(row.days_left)} дн. назад`
                            : `${row.days_left} дн.`}
                      </span>
                    </td>
                    <td>
                      {row.monthly_price_rub
                        ? `${formatRub(row.monthly_price_rub)} ₽/мес`
                        : "—"}
                    </td>
                    {showClients ? <td>{row.clients ?? 0}</td> : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
